import { useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';
import useErrorHandler from './useErrorHandler';

/**
 * Custom hook for optimized data fetching with caching and error handling
 */
export const useOptimizedFetch = (url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { executeWithErrorHandling } = useErrorHandler();
  
  // Cache for storing fetched data
  const cacheRef = useRef(new Map());
  const abortControllerRef = useRef(null);

  const {
    dependencies = [],
    cacheKey = url,
    cacheTTL = 5 * 60 * 1000, // 5 minutes default
    enabled = true,
    retryAttempts = 3,
    retryDelay = 1000
  } = options;

  const fetchData = useCallback(async () => {
    if (!enabled || !url) {
      return;
    }

    // Check cache first
    const cachedData = cacheRef.current.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < cacheTTL) {
      setData(cachedData.data);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const response = await authService.authenticatedRequest(url, {
        ...options,
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        // For 401 errors, don't try to parse as JSON
        if (response.status === 401) {
          throw new Error(`Authentication failed: ${response.statusText}`);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.warn(`Non-JSON response from ${url}:`, { contentType, text: text.substring(0, 200) });
        throw new Error(`Expected JSON response but got ${contentType}. Response: ${text.substring(0, 200)}`);
      }
      
      const result = await response.json();
      
      // Cache the result
      cacheRef.current.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      setData(result);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('useOptimizedFetch error:', {
          url,
          error: error.message,
          stack: error.stack
        });
        setError(error);
        // Don't throw the error to prevent unhandled promise rejections
        // The error is already set in state for components to handle
      }
    } finally {
      setLoading(false);
    }
  }, [url, cacheKey, cacheTTL, enabled, ...dependencies]);

  const refetch = useCallback(() => {
    // Clear cache for this key
    cacheRef.current.delete(cacheKey);
    return fetchData();
  }, [fetchData, cacheKey]);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  // Standalone fetch function for making individual requests
  const fetchWithOptimization = useCallback(async (url, options = {}) => {
    try {
      const response = await authService.authenticatedRequest(url, {
        ...options,
        signal: abortControllerRef.current?.signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(`Authentication failed: ${response.statusText}`);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      console.error('fetchWithOptimization error:', {
        url,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }, []);

  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (enabled && url) {
      executeWithErrorHandling(fetchData);
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, enabled, executeWithErrorHandling]);

  return {
    data,
    loading,
    error,
    refetch,
    clearCache,
    fetchWithOptimization
  };
};

export default useOptimizedFetch;
